import * as React from 'react';
import { connect } from 'react-redux';
import { ca } from '@cian/analytics';
import { RouteComponentProps, useLocation } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { TDeviceType, useDeviceType } from "@cian/ui-kit";

import { IUser } from '../../types/user';
import { IPosts } from '../../types/posts';
import { IBreadItem } from '../../types/bread_crumb';
import { IGlobalErrorState } from '../../types/global_error';
import { getBlogs, setCategory } from '../../reducers/modules/blogs/blogs';
import { List } from '../../components/list/list';
import { LoadError } from '../../components/load_error/load_error';
import { BreadCrumbs } from '../bread_crumbs/bread_crumbs';
import { Aside } from '../aside/aside';
import { setBreadCrumb } from '../../reducers/modules/bread_crumbs/bread_crumbs';
import { saveScroll } from '../../reducers/modules/scroll/scroll';
import { IScroll } from '../../types/scroll';
import { getDiscussed } from '../../reducers/modules/discussed/discussed';
import { getRegions } from '../../reducers/modules/regions/regions';
import { BlogListItem } from '../../components/blog_list_item/blog_list_item';
import { scrollComments } from '../../reducers/modules/scroll_comment/scroll_comment';
import { IConfig } from '../../types/config';
import { combineDataLayerObject } from '../../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../../app/helpers/breadcrumbs_helper';
import { IAnalytics } from "../../types/analytics";
import { IApplicationState, IThunkContext, TReduxActions } from "../../types/redux";

import s from './blogs.css';

interface IProps {
  user: IUser;
  blogs: IPosts;
  scroll: IScroll;
  globalError: IGlobalErrorState;
  config: IConfig;
  deviceType: TDeviceType;
  analytics: IAnalytics;
}

interface IBlogsProps extends IProps {
  routeCategory: string;
}

interface IDispatchProps {
  getBlogs(params: any): void;
  getRegions(scheme: string, host: string): void;
  setCategory(category: string): void;
  setBreadCrumb(path: IBreadItem[]): void;
  getDiscussed(regionID: string): void;
  saveScroll(item: any): void;
  scrollComments(): void;
}

export const setCrumbsBlogs = (tag: string, search: string, category: string) => {
  const crumbs: IBreadItem[] = [
    {path: '/magazine'},
    {path: '/blogs'},
  ];
  if (!!tag) {
    const name = tag[0].toUpperCase() + tag.substr(1);

    crumbs.push({
      path: tag,
      url: `/blogs?tag=${tag}`,
      name,
    });
  } else if (!!search) {
    crumbs.push({
      path: search,
      url: `/blogs?search=${search}`,
      name: 'Поиск',
    });
  } else if (!!category) {
    crumbs.push({path: `b-${category}`});
  }

  return crumbs;
}

const BlogsContainer: React.FC<IBlogsProps & IDispatchProps & RouteComponentProps> = props => {
  const setAuthorBreadcrumb = (
    authorName: string,
    authorID: string,
  ) => {
    const crumbs: IBreadItem[] = [
      { path: '/magazine' },
      { path: '/blogs' },
      {
        path: authorName,
        url: `/blogs?author_id=${authorID}`,
        name: authorName,
      },
    ];

    props.setBreadCrumb(crumbs);
  };

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tag = query.get("tag") || '';
  const search = query.get("search") || '';
  const ordering = query.get("ordering") || '-date_publish';

  const { config, routeCategory } = props;
  const deviceType = useDeviceType();
  const page = query.get("page") ? parseInt(query.get("page") as string) : 1;
  const regionID = config.regionID && config.regionID !== '0' ? config.regionID : '';
  const category = routeCategory ? routeCategory : query.get("category") || '';
  const authorID = query.get("author_id") || '';

  React.useEffect(() => {
    const params = {page, ordering, tag, category, authorID, search, regionID};

    const crumbs = setCrumbsBlogs(tag, search, category);

    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(props.analytics, getBreadCrumbsNames(crumbs), deviceType));
    }

    props.getBlogs(params);
    props.getRegions(config.regionsApiSchemeNode, config.regionsApiHostNode);
    props.setBreadCrumb(crumbs);
    props.getDiscussed(regionID);
    props.setCategory(category);
  }, [tag, search, ordering, page, regionID, category, authorID]);

  const {
    items,
    pages_count: pagesCount,
    count: recordsCount,
  } = props.blogs;
  const { item: selectedItem } = props.scroll;

  let authorName;
  let companyName;
  let specialistLink;

  if (query.get("author_id") && (items.length > 0) && items[0].attributes.authorObject) {
    authorName = items[0].attributes.authorObject!.fullName;
    companyName = items[0].attributes.authorObject!.companyName;
    specialistLink = items[0].attributes.authorObject!.specialistLink;
  }

  if (authorName) {
    setAuthorBreadcrumb(
      authorName || '',
      query.get("author_id") || '',
    );
  }

  function loadMore(pageN: number) {
    const page = pageN || 1;
    const category = props.blogs.currentCategory || '';
    const authorID = query.get("author_id") || '';
    const regionID = props.config.regionID && props.config.regionID !== '0' ? props.config.regionID : '';
    const params = { page, ordering, tag, category, authorID, search, regionID };

    props.getBlogs(params);
  }

  function seoHideBlock() {
    return (
      <span className={s['blogs__schema']}>
        <span itemProp="headline">Блоги риэлторов и других специалистов рынка недвижимости России - ЦИАН</span>
        <span itemProp="description">
          ЦИАН - авторские колонки, блоги специалистов рынка недвижимости, блоги риэлторов. Обсуждения, комментарии.
        </span>
      </span>
    );
  }

  return (
    <div className={s['blogs-wrap']}>
      <div itemScope itemType="https://schema.org/Blog" className={`${s['blogs']} cg-col-16 cg-col-xs-16`}>
        {seoHideBlock()}
        <BreadCrumbs />
        {
          props.globalError.error ?
            <LoadError /> :
            <List
                currentUser={props.user}
                items={items}
                pagesCount={pagesCount}
                recordsCount={recordsCount}
                searchTag={tag}
                author={authorName || companyName}
                saveScroll={props.saveScroll}
                selectedItem={selectedItem}
                listItemClass={BlogListItem}
                scrollComments={props.scrollComments}
                specialistLink={specialistLink}
                search={search}
                onLoadMore={(page: number) => loadMore(page)} />
        }
      </div>
      <Aside />
    </div>
  );
}

function mapStateToProps(state: IApplicationState): IProps {
  const {
    blogs,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  } = state;

  return {
    blogs,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch<IApplicationState, IThunkContext, TReduxActions>): IDispatchProps {
  return {
    getBlogs: (params: any) => {
      dispatch(getBlogs(params));
    },
    getRegions: (scheme: string, host: string) => {
      dispatch(getRegions(scheme, host));
    },
    setCategory: (category: string) => {
      dispatch(setCategory(category));
    },
    setBreadCrumb: (path: IBreadItem[]) => {
      dispatch(setBreadCrumb(path));
    },
    getDiscussed: (regionID: string) => {
      dispatch(getDiscussed(regionID));
    },
    saveScroll: (item: any) => {
      dispatch(saveScroll(item));
    },
    scrollComments: () => {
      dispatch(scrollComments());
    },
  }
}

const mergePropsSpecialist = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IBlogsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'specialist'}, stateProps, dispatchProps, ownProps)
}

const mergePropsMarketTrend = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IBlogsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'market_trend'}, stateProps, dispatchProps, ownProps)
}

export const Blogs = connect(mapStateToProps, mapDispatchToProps)(BlogsContainer);
export const SpecBlogs = connect(mapStateToProps, mapDispatchToProps, mergePropsSpecialist)(BlogsContainer);
export const MarketBlogs = connect(mapStateToProps, mapDispatchToProps, mergePropsMarketTrend)(BlogsContainer);
